/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { Suspense, useMemo } from 'react';
import { Box, CssBaseline, Link, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useConfiguration } from '../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../redux/enums/ConfigResourceProps';
import { isFeatureEnabled, ToggleableFeature } from '../shared/utils/featuresUtils';
import Header from './header/Header';
import Backdrop from '../components/backdrop/Backdrop';
import Footer from './footer/Footer';
import useSidebar, { SidebarContext } from './sidebar/useSidebar';
import ConfigurableFooter from './configurableFooter/configurableFooter';
import { useUserAccounts } from '../redux/features/user/userAccounts/useUserAccounts';

const useStyles = theme =>
  makeStyles({
    ...theme.customTheme,
    homeBackground: {
      height: '100vh',
      background: theme.palette?.homeBackground || theme.bgHome,
    },
    innerBackground: {
      backgroundColor: theme.palette.mainBackground,
    },
    skipLink: {
      position: 'absolute',
      height: '3.438rem',
      left: '-9999px',
      zIndex: '999',
      opacity: '0',
      backgroundColor: theme.palette.primary.main,
      textAlign: 'center',
      color: theme.palette.white,
      padding: '0',
      fontSize: '1rem',
      textDecoration: 'none',
      '&:focus': {
        top: '0',
        left: '0',
        right: '0',
        opacity: '1',
        padding: '1rem',
        zIndex: '9999',
        transform: 'translateX(0%)',
      },
      '&:visited': {
        color: theme.palette.white,
      },
    },
    submissionLayout: {
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: 'calc(100% * 2 / 3)',
      },
      [theme.breakpoints.up('xl2')]: {
        width: 'calc(100% * 3 / 4)',
      },
      '& > .submissionContainer': {
        padding: '1rem',
        margin: '.5rem 0 1rem 0',
        [theme.breakpoints.up('md')]: {
          padding: '1.5rem',
        },
        [theme.breakpoints.up('lg')]: {
          margin: '.5rem 0 1.5rem 0',
        },
        [theme.breakpoints.up('xl2')]: {
          margin: '.5rem 3.5% 3rem 3.5%',
        },
      },
      '& > .breadcrumbs': {
        gridArea: 'breadcrumbs',
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(3),
      },
    },
    reducedWidthContainer: {
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: '55%', // optimize with flex
      },
      [theme.breakpoints.up('xl2')]: {
        width: 'calc(100% * 2 / 3)',
      },
      '& > .breadcrumbs': {
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(3),
      },
    },
    container: ({ isExpandedDrawer }) => ({
      display: 'flex',
      flexDirection: 'column',
      padding: '1rem',
      margin: '1rem 0',
      minHeight: 'calc(100vh - 10.1875rem)', // 100% of viewport - (header + footer)
      [theme.breakpoints.up('md')]: {
        padding: '1.5rem',
      },
      [theme.breakpoints.down('sm')]: {
        margin: '.5rem 0',
        minHeight: '100vh',
      },
      [theme.breakpoints.up('lg')]: {
        minHeight: 'calc(100vh - 9.5rem)',
        marginY: '1.5rem',
        marginLeft: isExpandedDrawer ? '17.5rem' : '8.75rem',
        transition: 'margin 0.2s ease',
      },
      [theme.breakpoints.up('xl2')]: {
        minHeight: 'calc(100vh -  9.5rem)',
        marginY: '3rem',
        marginLeft: isExpandedDrawer ? '17.2rem' : '8.5rem',
      },
      [theme.breakpoints.up('xl4')]: {
        minHeight: 'calc(100vh - 15rem)',
        margin: '3.5rem 12.26%',
      },
      '& > .breadcrumbs': {
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(3),
      },
    }),
    containerBig: ({ isExpandedDrawer }) => ({
      display: 'flex',
      flexDirection: 'column',
      minHeight: 'calc(100vh - 10.1875rem)', // 100% of viewport - (header + footer)
      [theme.breakpoints.down('sm')]: {
        margin: '.5rem 0',
        minHeight: '100vh',
      },
      [theme.breakpoints.up('lg')]: {
        minHeight: 'calc(100vh - 9.5rem)',
        marginY: '1.5rem',
        marginLeft: isExpandedDrawer ? '17.5rem' : '5rem',
        transition: 'margin 0.2s ease',
      },
      [theme.breakpoints.up('xl2')]: {
        minHeight: 'calc(100vh -  9.5rem)',
        marginY: '3rem',
        marginLeft: isExpandedDrawer ? '17.2rem' : '5rem',
      },
      '& > .wideContainer': {
        padding: theme.spacing(4),
        margin: '.5rem 0 1rem 0',
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(6),
        },
        [theme.breakpoints.up('lg')]: {
          margin: '.5rem 0 1.5rem 0',
        },
        [theme.breakpoints.up('xl2')]: {
          margin: '.5rem 3.5% 3rem 3.5%',
        },
        [theme.breakpoints.up('xl4')]: {
          minHeight: 'calc(100vh - 15rem)',
          margin: '3.5rem 12.26%',
        },
        '& > .breadcrumbs': {
          marginTop: theme.spacing(0.5),
          marginBottom: theme.spacing(3),
        },
      },
    }),
    footer: ({ isExpandedDrawer }) => ({
      [theme.breakpoints.down('sm')]: {
        margin: '.5rem 0',
        minHeight: 'auto',
      },
      [theme.breakpoints.up('lg')]: {
        marginY: '1.5rem',
        marginLeft: isExpandedDrawer ? '17.5rem' : '5rem',
        transition: 'margin 0.2s ease',
      },
      [theme.breakpoints.up('xl2')]: {
        marginY: '3rem',
        marginLeft: isExpandedDrawer ? '17.2rem' : '5rem',
      },
      [theme.breakpoints.up('xl4')]: {
        margin: '3.5rem 12.26%',
      },
    }),
  });

export default function Layout({ children }) {
  const sidebarHook = useSidebar();
  const { user, currentOrganization } = useUserAccounts();
  const { isExpandedDrawer } = sidebarHook;
  const theme = useTheme();
  const classes = useStyles(theme)({ isExpandedDrawer });
  const breakpointSmall = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const hasExtraBackground = useMemo(
    () => location.pathname === '/' || ['/search'].some(word => location.pathname.includes(word)),
    [location.pathname],
  );

  const mainPageContainer = useMemo(
    () =>
      location.pathname === '/' ||
      [
        '/search',
        '/user-profile',
        '/switch-account',
        '/accessibility-statement',
        '/epct',
        '/EP1001/ep',
      ].some(word =>
        location.pathname.includes(word),
      ),
    [location.pathname],
  );

  const wideContainer = useMemo(
    () =>
      [
        '/organisation', '/automatic-registration', '/receipts', '/contact',
        // tb refactored: submissionPreview
      ].some(word => location.pathname.includes(word)),
    [location],
  );

  const submissionLayout = useMemo(
    () => ['/draft', '/signing', '/payment'].some(word => location.pathname.includes(word)),
    [location],
  );

  const reducedWidthContainer = useMemo(() => ['/user-profile'].some(word => location.pathname.includes(word)), [location]);

  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const configurableFooterEnabled = isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.FOOTER);
  const configurableHeaderEnabled = isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.HEADER);
  const showConfigurableFooter = useMemo(() => location.pathname === '/', [location.pathname]);

  return (
    <SidebarContext.Provider value={sidebarHook}>
      <Box className={hasExtraBackground ? classes.homeBackground : classes.innerBackground}>
        <Suspense fallback={<Backdrop blocking={true} />}>
          <Backdrop />
          <CssBaseline />
          {mainPageContainer && (
            <Link href="#main" underline="always" variant="body2" className={classes.skipLink}>
              Skip to main
            </Link>
          )}
          <Header
            breakpointSmall={breakpointSmall}
            showConfigurableHeader={configurableHeaderEnabled}
            user={user}
            currentOrganization={currentOrganization}
          />
          <main
            id="main"
            className={clsx({
              [classes.container]: mainPageContainer,
              [classes.containerBig]: wideContainer,
              [classes.submissionLayout]: submissionLayout,
              [classes.reducedWidthContainer]: reducedWidthContainer,
            })}
          >
            {children}
          </main>
          {showConfigurableFooter && configurableFooterEnabled && <ConfigurableFooter className={classes.footer} />}
          <Footer />
        </Suspense>
      </Box>
    </SidebarContext.Provider>
  );
}
